/**
 * Pure date / timezone helpers for the public booking flow.
 *
 * Every instant crossing the API is an ISO UTC string; everything the visitor
 * reads is rendered in a display timezone they chose. These helpers are the
 * only place that translates between the two, so they are unit tested.
 */

/** `YYYY-MM-DD` in some timezone. Never a UTC instant. */
export type DayKey = string;
/** `YYYY-MM` in some timezone. */
export type MonthKey = string;

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const DAY_MS = 86_400_000;

function pad(value: number, size = 2): string {
  return String(Math.abs(value)).padStart(size, '0');
}

/** Wall-clock fields of `instant` as read in `timeZone`. */
export function zonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type);
    return found ? Number(found.value) : 0;
  };

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
  };
}

/** The zone's offset from UTC at `instant`, in milliseconds (east positive). */
export function zoneOffsetMs(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** `YYYY-MM-DD` of an instant, as seen in `timeZone`. */
export function dayKeyInZone(instant: Date | string, timeZone: string): DayKey {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (!Number.isFinite(date.getTime())) return '';
  const p = zonedParts(date, timeZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

/** The UTC instant of local midnight starting `day` in `timeZone`. */
export function zonedStartOfDayUtc(day: DayKey, timeZone: string): Date {
  const naive = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(naive)) return new Date(Number.NaN);
  // Two passes: the first offset may belong to the wrong side of a DST edge.
  let guess = naive - zoneOffsetMs(new Date(naive), timeZone);
  guess = naive - zoneOffsetMs(new Date(guess), timeZone);
  return new Date(guess);
}

export function monthKeyOf(day: DayKey): MonthKey {
  return day.slice(0, 7);
}

export function monthKeyInZone(instant: Date, timeZone: string): MonthKey {
  return monthKeyOf(dayKeyInZone(instant, timeZone));
}

function splitMonth(month: MonthKey): { year: number; month: number } {
  const [year, monthPart] = month.split('-');
  return { year: Number(year), month: Number(monthPart) };
}

export function addMonths(month: MonthKey, delta: number): MonthKey {
  const { year, month: m } = splitMonth(month);
  if (!Number.isFinite(year) || !Number.isFinite(m)) return month;
  const index = year * 12 + (m - 1) + delta;
  return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1)}`;
}

export function daysInMonth(month: MonthKey): number {
  const { year, month: m } = splitMonth(month);
  return new Date(Date.UTC(year, m, 0)).getUTCDate();
}

/** First day of `month` as a day key. */
export function firstDayOf(month: MonthKey): DayKey {
  return `${month}-01`;
}

/** Last day of `month` as a day key. */
export function lastDayOf(month: MonthKey): DayKey {
  return `${month}-${pad(daysInMonth(month))}`;
}

/**
 * The UTC window that covers `month` in `timeZone`, i.e. from local midnight
 * on the 1st to local midnight on the 1st of the next month.
 */
export function monthRangeUtc(
  month: MonthKey,
  timeZone: string,
): { from: Date; to: Date } {
  return {
    from: zonedStartOfDayUtc(firstDayOf(month), timeZone),
    to: zonedStartOfDayUtc(firstDayOf(addMonths(month, 1)), timeZone),
  };
}

/** Sunday = 0 … Saturday = 6, for a day key. */
export function weekdayOf(day: DayKey): number {
  const parsed = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(parsed) ? new Date(parsed).getUTCDay() : 0;
}

export function addDays(day: DayKey, delta: number): DayKey {
  const parsed = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return day;
  const next = new Date(parsed + delta * DAY_MS);
  return `${pad(next.getUTCFullYear(), 4)}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

/**
 * Which weekday a calendar starts on for a locale. Sunday for `en`, Monday
 * everywhere else the product ships. Deliberately data, not `Intl.Locale`
 * `weekInfo`, which is still not implemented everywhere.
 */
export function weekStartFor(locale: string): 0 | 1 {
  return locale.toLowerCase().startsWith('en') ? 0 : 1;
}

/**
 * Six fixed rows of seven day keys covering `month`, padded with the
 * neighbouring months so the grid never reflows between months.
 */
export function buildMonthGrid(month: MonthKey, weekStart: 0 | 1): DayKey[][] {
  const first = firstDayOf(month);
  const lead = (weekdayOf(first) - weekStart + 7) % 7;
  const start = addDays(first, -lead);
  const weeks: DayKey[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const row: DayKey[] = [];
    for (let index = 0; index < 7; index += 1) {
      row.push(addDays(start, week * 7 + index));
    }
    weeks.push(row);
  }
  return weeks;
}

/** Slot instants bucketed by the day they fall on in `timeZone`, kept sorted. */
export function groupSlotsByDay(
  slots: readonly string[],
  timeZone: string,
): Map<DayKey, string[]> {
  const grouped = new Map<DayKey, string[]>();
  for (const slot of [...slots].sort()) {
    const key = dayKeyInZone(slot, timeZone);
    if (!key) continue;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(slot);
    else grouped.set(key, [slot]);
  }
  return grouped;
}

/** True when `day` belongs to `month`. */
export function isInMonth(day: DayKey, month: MonthKey): boolean {
  return monthKeyOf(day) === month;
}

/** A short label for a timezone, e.g. `GMT-3` — never a bare offset number. */
export function zoneShortLabel(
  timeZone: string,
  locale: string,
  instant: Date = new Date(),
): string {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: 'shortOffset',
  }).formatToParts(instant);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
}

/** The visitor's own zone, with a safe fallback when `Intl` returns nothing. */
export function visitorTimeZone(): string {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return resolved && resolved.length > 0 ? resolved : 'UTC';
}

/** True when the zone identifier is one `Intl` will accept. */
export function isValidTimeZone(timeZone: string): boolean {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** The zones offered in the switcher: visitor, professional, UTC, then all. */
export function timeZoneOptions(visitor: string, professional: string): string[] {
  const intl = Intl as { supportedValuesOf?: (key: string) => string[] };
  const all = typeof intl.supportedValuesOf === 'function'
    ? intl.supportedValuesOf('timeZone')
    : [];
  const preferred = [visitor, professional, 'UTC'].filter(isValidTimeZone);
  return [...new Set([...preferred, ...all])];
}
